// BidSheet's counterpart while the instant add board is open: no budget, no
// bid amount, calls instant_add_team directly for an immediate first come,
// first served swap. Rendered whenever isInstantAddOpen says so: preseason, a
// late draft's grace window, and the weekly window from the 6am Tuesday
// waiver run to the next bidding window at 10am Sunday (migrations 069 and
// 076). The RPC enforces the same windows.
//
// A team whose game has started cannot be dropped (migration 076, the same
// rule as the lineup lock), so those are listed but disabled rather than
// offered and then refused. lockedSlugs comes from the caller, which already
// holds the week's games.
import React, { useState } from "react";
import BottomSheet from "../sheets/BottomSheet";
import { supabase } from "../../supabase/supabase";
import { hapticImpact } from "../../capacitor/haptics";
import { invalidateLeague } from "../../utils/queryCache";

export default function InstantAddSheet({ leagueId, teamSlug, teamName, myRoster, lockedSlugs = new Set(), allTeams, onClose, onAdded }) {
  const hasOpenSlot = myRoster.length < 7;
  const nothingDroppable = !hasOpenSlot && myRoster.every((slug) => lockedSlugs.has(slug));
  const [dropSlug, setDropSlug] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    if (!hasOpenSlot && !dropSlug) return;
    setSubmitting(true);
    setResult(null);
    const { data, error } = await supabase.rpc("instant_add_team", {
      p_league_id: leagueId,
      p_team_id: teamSlug,
      p_drop_team_id: hasOpenSlot ? null : dropSlug,
    });
    setSubmitting(false);
    const res = error ? { error: error.message } : data;
    setResult(res);
    if (res?.success) {
      hapticImpact('light');
      // The swap rewrites this member's roster, which the Lineup, League and
      // Scoreboard tabs all hold cached.
      invalidateLeague(leagueId);
      onAdded?.();
      setTimeout(onClose, 800);
    }
  };

  return (
    <BottomSheet open={!!teamSlug} onClose={onClose} title={`Add ${teamName}`}>
      {!hasOpenSlot && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--color-text-muted)", marginBottom: 6 }}>
            Drop from your roster
          </label>
          <select
            value={dropSlug}
            onChange={(e) => setDropSlug(e.target.value)}
            style={{
              width: "100%", height: 44, padding: "0 12px", borderRadius: 12,
              border: "1px solid var(--color-border)", fontSize: 15, color: "var(--color-text-strong)",
            }}
          >
            <option value="">Select a team to drop</option>
            {myRoster.map((slug) => (
              <option key={slug} value={slug} disabled={lockedSlugs.has(slug)}>
                {allTeams?.[slug]?.school || slug}{lockedSlugs.has(slug) ? " (game started)" : ""}
              </option>
            ))}
          </select>
          {nothingDroppable && (
            <p style={{ fontSize: 13, color: "var(--color-warning-text)", margin: "8px 0 0" }}>
              Every team on your roster has started its game, so there is nothing to drop this week.
            </p>
          )}
        </div>
      )}

      <p style={{ fontSize: 13, color: "var(--color-text-muted)", marginBottom: 16 }}>
        Pickups are instant and first come, first served. No bid, no wait.
      </p>

      {result?.error && (
        <div style={{ backgroundColor: "var(--color-danger-bg)", border: "1px solid var(--color-danger-border)", borderRadius: 12, padding: "10px 14px", marginBottom: 16 }}>
          <p style={{ fontSize: 14, color: "var(--color-danger-text)", margin: 0 }}>{result.error}</p>
        </div>
      )}

      {result?.success && (
        <div style={{ backgroundColor: "var(--color-success-bg)", border: "1px solid var(--color-success-border)", borderRadius: 12, padding: "10px 14px", marginBottom: 16 }}>
          <p style={{ fontSize: 14, color: "var(--color-success-text)", margin: 0 }}>{teamName} added to your roster.</p>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={submitting || nothingDroppable || (!hasOpenSlot && !dropSlug)}
        style={{
          width: "100%", padding: "14px 0", borderRadius: 14, border: "none",
          backgroundColor: "var(--color-brand)", color: "var(--color-bg-card)", fontSize: 16, fontWeight: 700,
          cursor: "pointer", opacity: submitting || nothingDroppable || (!hasOpenSlot && !dropSlug) ? 0.5 : 1,
        }}
      >
        {submitting ? "Adding..." : "Add team"}
      </button>
    </BottomSheet>
  );
}
